import { useEffect } from 'react';
import {
    isStorageItemExpired,
    loadFromStorageWithExpiration,
    removeFromStorage,
    saveToStorageWithExpiration,
} from '../../../../../library/local-storage/local-storage';
import { getFirstPhaseStep, getPhaseStepByDish } from '../../shared/utils/phase';

const STORAGE_PREFIX_KEY = 'cart_item_meals';
const STORAGE_ITEM_EXPIRATION_DAYS = 14;

const MILISECONDS_IN_ONE_DAY = 86400000;

const lsCartMealsKey = (cartItemId, dietId, phaseId) => `${STORAGE_PREFIX_KEY}:${cartItemId}:${dietId}:${phaseId}`;

const getCartItemMealLsExpiration = () => STORAGE_ITEM_EXPIRATION_DAYS * MILISECONDS_IN_ONE_DAY;

// Custom hook zajišťje ukládání vybraných jídel do local Storage.
export const useCartMealsLocalStorage = () => {
    // Uloží vybrané produkty v rámci kroku fáze
    const saveProductsToStorage = (cartItemId, dietId, phaseId, phaseStep, products, onSaveProducts) => {
        const prevSavedItems = loadProductsFromStorage(cartItemId, dietId, phaseId);

        const updatedSavedItems = {
            ...(prevSavedItems ? { ...prevSavedItems } : undefined),
            [phaseStep]: products,
        };

        saveToStorageWithExpiration(
            lsCartMealsKey(cartItemId, dietId, phaseId),
            updatedSavedItems,
            getCartItemMealLsExpiration()
        );

        if (onSaveProducts) {
            onSaveProducts();
        }
    };

    // Uloží vybrané produkty v rámci fáze
    const savePhaseProductsToStorage = (cartItemId, dietId, phaseId, products) => {
        saveToStorageWithExpiration(
            lsCartMealsKey(cartItemId, dietId, phaseId),
            products,
            getCartItemMealLsExpiration()
        );
    };

    const savePickedPackageProductsToStorage = (
        cartItemId,
        packageId,
        pickedProductsApi,
        phaseSteps,
        onSavePickedProducts
    ) => {
        const products = phaseSteps.reduce(
            (prevPickedProducts, phaseStep) => ({ ...prevPickedProducts, [phaseStep]: [] }),
            {}
        );

        pickedProductsApi.forEach((product) => {
            const phaseStep = getPhaseStepByDish(product.dishType);

            products[phaseStep] = [
                ...(products[phaseStep] ? products[phaseStep] : []),
                { id: product.id, amount: product.amount, title: product.name },
            ];
        });

        saveToStorageWithExpiration(
            lsCartMealsKey(cartItemId, null, packageId),
            products,
            getCartItemMealLsExpiration()
        );

        onSavePickedProducts(products);
    };

    // Uloží všechny již vybrané (mód editace) produkty do lokálního uložiště
    const savePickedProductsToStorage = (
        cartItemId,
        dietId,
        phases,
        pickedProductsApi,
        activePhase,
        onSavePickedProducts
    ) => {
        let products = {};

        phases.forEach((phase) => {
            products = { ...products, [phase.id]: {} };
        });

        const filteredPickedProducts = pickedProductsApi.filter((product) => products[product.phaseId] !== undefined);

        filteredPickedProducts.forEach((product) => {
            const phaseStep = getPhaseStepByDish(product.dishType);
            products[product.phaseId] = {
                ...products[product.phaseId],
                [phaseStep]: [
                    ...(products[product.phaseId][phaseStep] ? products[product.phaseId][phaseStep] : []),
                    { id: product.id, amount: product.amount, title: product.name },
                ],
            };
        });

        phases.forEach((phase) => {
            saveToStorageWithExpiration(
                lsCartMealsKey(cartItemId, dietId, phase.id),
                products[phase.id],
                getCartItemMealLsExpiration()
            );
        });

        onSavePickedProducts(products[activePhase.id][getFirstPhaseStep(activePhase)]);
    };

    // načteme produkty fáze z lokálního uložiště
    const loadProductsFromStorage = (cartItemId, dietId, phaseId) =>
        loadFromStorageWithExpiration(lsCartMealsKey(cartItemId, dietId, phaseId));

    const loadAllProductsFromStorage = (cartItemId, dietId, phases) => {
        const products = {};

        phases.forEach((phase) => {
            products[phase] = loadFromStorageWithExpiration(lsCartMealsKey(cartItemId, dietId, phase));
        });

        return products;
    };

    // Odstraníme produkty fáze z lokálního uložiště
    const removeProductsFromStorage = (cartItemId, dietId, phaseIds) => {
        phaseIds.forEach((phaseId) => {
            removeFromStorage(lsCartMealsKey(cartItemId, dietId, phaseId));
        });
    };

    // Check zdali nemáme v lokálním uložišti expirované položky, pokud ano, smažeme
    useEffect(() => {
        Object.keys(localStorage)
            .filter((key) => key.includes(STORAGE_PREFIX_KEY) && isStorageItemExpired(key))
            .forEach((key) => {
                // update saving product items under key 'STORAGE_PREFIX_KEY' to remove necessary?
                removeFromStorage(key);
            });
    }, []);

    // Zkontroluje, zdali dieta (popř. fáze, pokud dietId === null) má v lokálním uložišti produkty
    const isLocalStorageClear = (cartItemId, dietId, phaseIds) =>
        !phaseIds.some((phaseId) => !!loadProductsFromStorage(cartItemId, dietId, phaseId));

    return {
        saveProductsToStorage,
        loadProductsFromStorage,
        removeProductsFromStorage,
        savePickedProductsToStorage,
        loadAllProductsFromStorage,
        isLocalStorageClear,
        savePhaseProductsToStorage,
        savePickedPackageProductsToStorage,
    };
};
